// apps/web/src/pages/coletor/kanban-tablet-configurar-page.tsx — Fase 11, Plano 15 (KAN-06, D-21).
// Configuração única do tablet: vincula a credencial do dispositivo e escolhe as células.
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { apiFetch, apiMutate } from '../../lib/csrf-fetch';
import {
  expiracaoPadrao,
  limparCredencial,
  obterCredencial,
  salvarCredencial,
  type CredencialDispositivo,
} from '../../lib/kanban/dispositivo';
import { obterEstatisticasOutbox } from '../../lib/offline/outbox';
import { obterSessaoAtual } from '../../lib/session';

interface CelulaOpcao {
  id: string;
  codigo: string;
  nome: string;
}

const MSG_SEM_PERMISSAO = 'Somente administradores podem configurar o tablet.';

/** Revoga o dispositivo no servidor (POST /devices/:id/revoke, exige users:manage). */
async function revogarNoServidor(deviceId: string): Promise<Response> {
  return apiMutate(`/api/v1/devices/${encodeURIComponent(deviceId)}/revoke`, { method: 'POST' });
}

export function KanbanTabletConfigurarPage(): ReactElement {
  const [vinculo, setVinculo] = useState<CredencialDispositivo | null>(null);
  const [celulas, setCelulas] = useState<CelulaOpcao[]>([]);
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [nome, setNome] = useState<string>('');
  const [carregando, setCarregando] = useState<boolean>(true);
  const [salvando, setSalvando] = useState<boolean>(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  // WR-WEB-01: só quem tem users:manage vincula/desvincula (a API já recusa o resto).
  const [podeConfigurar, setPodeConfigurar] = useState<boolean>(false);
  const [pendentesAoDesvincular, setPendentesAoDesvincular] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setVinculo(await obterCredencial());
      const sessao = await obterSessaoAtual();
      setPodeConfigurar(sessao.autenticado && sessao.usuario.permissions.includes('users:manage'));
      const res = await apiFetch<{ data: CelulaOpcao[] }>('/api/v1/planejamento/celulas');
      setCelulas(res.data ?? []);
    } catch {
      setErro('Não foi possível carregar as células.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  function alternar(codigo: string): void {
    setSelecionadas((atual) => (atual.includes(codigo) ? atual.filter((c) => c !== codigo) : [...atual, codigo]));
  }

  async function vincular(): Promise<void> {
    if (nome.trim().length === 0) {
      setErro('Informe o nome do tablet.');
      return;
    }
    if (selecionadas.length === 0) {
      setErro('Selecione ao menos uma célula.');
      return;
    }
    setSalvando(true);
    setErro(null);
    setSucesso(null);
    let idCriado: string | null = null;
    let vinculoConcluido = false;
    try {
      const resLink = await apiMutate('/api/v1/devices/link', {
        method: 'POST',
        body: { label: nome.trim(), scope: 'kanban-tablet', expiresAt: expiracaoPadrao(new Date()) },
      });
      if (resLink.status === 403 || resLink.status === 401) {
        setErro(MSG_SEM_PERMISSAO);
        return;
      }
      if (!resLink.ok) throw new Error('link');
      const link = (await resLink.json()) as { id: string; credential: string };
      idCriado = link.id;

      const resCelulas = await apiMutate(`/api/v1/devices/${encodeURIComponent(link.id)}/celulas`, {
        method: 'PUT',
        body: { celulas: selecionadas },
      });
      if (resCelulas.status === 403 || resCelulas.status === 401) {
        setErro(MSG_SEM_PERMISSAO);
        return;
      }
      if (!resCelulas.ok) throw new Error('celulas');

      await salvarCredencial({ deviceId: link.id, credential: link.credential, label: nome.trim() });
      vinculoConcluido = true;
      setVinculo(await obterCredencial());
      setSucesso('Tablet vinculado. Abra o Kanban do operador para ver a fila.');
    } catch {
      setErro('Não foi possível vincular o tablet. Tente novamente.');
    } finally {
      // WR-WEB-02: credencial emitida que não chegou a ser salva (falha em PUT celulas ou no
      // IndexedDB) é revogada para não sobrar dispositivo órfão com credencial válida por 1 ano.
      await revogarOrfao(idCriado, vinculoConcluido);
      setSalvando(false);
    }
  }

  async function revogarOrfao(idCriado: string | null, vinculoConcluido: boolean): Promise<void> {
    if (!idCriado || vinculoConcluido) return;
    try {
      await revogarNoServidor(idCriado);
    } catch {
      // Best effort: sem rede, o administrador ainda pode revogar pela API.
    }
  }

  async function desvincular(forcar: boolean): Promise<void> {
    if (!vinculo) return;
    setErro(null);
    setSucesso(null);
    if (!forcar) {
      // WR-WEB-01: apontamentos pendentes ficariam órfãos sem credencial para enviar.
      const { pendentes } = await obterEstatisticasOutbox().catch(() => ({ pendentes: 0 }));
      if (pendentes > 0) {
        setPendentesAoDesvincular(pendentes);
        return;
      }
    }
    setPendentesAoDesvincular(null);
    try {
      const res = await revogarNoServidor(vinculo.deviceId);
      if (res.status === 401 || res.status === 403) {
        setErro(MSG_SEM_PERMISSAO);
        return;
      }
      // 404: o dispositivo já não existe no servidor; só resta limpar o vínculo local.
      if (!res.ok && res.status !== 404) {
        setErro('O servidor não confirmou a revogação. O tablet continua vinculado.');
        return;
      }
    } catch {
      setErro('Sem conexão com o servidor. O tablet continua vinculado.');
      return;
    }
    await limparCredencial();
    setVinculo(null);
    setSucesso('Tablet desvinculado e credencial revogada no servidor.');
  }

  return (
    <div
      className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8 select-none"
      data-theme="dark-premium"
      data-density="touch"
    >
      <div className="max-w-2xl mx-auto space-y-6">
        <header className="border-b border-slate-800 pb-4">
          <span className="text-xs font-bold uppercase tracking-wider text-emerald-300">Kanban do operador</span>
          <h1 className="text-2xl font-bold text-white tracking-tight mt-0.5">Configurar este tablet</h1>
        </header>

        {erro && (
          <div className="p-4 bg-red-950/80 border border-red-500/50 text-red-200 rounded-xl text-sm" role="alert">
            {erro}
          </div>
        )}
        {sucesso && (
          <div className="p-4 bg-emerald-950/60 border border-emerald-500/40 text-emerald-200 rounded-xl text-sm" role="status">
            {sucesso}
          </div>
        )}

        {carregando ? (
          <div className="ui-skeleton h-24 w-full rounded-xl" />
        ) : vinculo ? (
          <section className="space-y-4 bg-slate-900 border border-slate-800 rounded-2xl p-5">
            <p className="text-sm text-slate-300">
              Vínculo atual: <span className="font-bold text-white">{vinculo.label}</span>
            </p>
            {pendentesAoDesvincular !== null && (
              <p className="text-sm text-amber-200 bg-amber-950/60 border border-amber-500/40 rounded-xl p-3" role="alert">
                Há {pendentesAoDesvincular} apontamento(s) salvo(s) neste tablet ainda não enviados. Se desvincular agora, eles
                não poderão ser enviados. Reconecte e aguarde o envio, ou confirme para desvincular mesmo assim.
              </p>
            )}
            {!podeConfigurar && (
              <p className="text-sm text-slate-400">{MSG_SEM_PERMISSAO}</p>
            )}
            <div className="flex flex-wrap gap-3">
              <a
                href="/coletor/kanban"
                className="min-h-[64px] inline-flex items-center px-6 bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-sm rounded-xl"
              >
                Abrir a fila da célula
              </a>
              {podeConfigurar && (
                <button
                  type="button"
                  onClick={() => void desvincular(pendentesAoDesvincular !== null)}
                  className="min-h-[64px] px-6 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-100 font-bold text-sm rounded-xl touch-manipulation"
                >
                  {pendentesAoDesvincular !== null ? 'Desvincular mesmo assim' : 'Desvincular este tablet'}
                </button>
              )}
            </div>
          </section>
        ) : !podeConfigurar ? (
          <section className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
            <p className="text-sm text-slate-300">{MSG_SEM_PERMISSAO} Entre como administrador para vincular este tablet.</p>
          </section>
        ) : (
          <section className="space-y-5 bg-slate-900 border border-slate-800 rounded-2xl p-5">
            <div className="space-y-2">
              <label htmlFor="nome-tablet" className="text-xs font-bold uppercase text-slate-300">
                Nome do tablet
              </label>
              <input
                id="nome-tablet"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className="w-full min-h-[56px] px-4 bg-slate-950 border border-slate-700 rounded-xl text-base text-white focus:ring-2 focus:ring-emerald-400"
              />
            </div>

            <fieldset className="space-y-2">
              <legend className="text-xs font-bold uppercase text-slate-300">Células deste tablet</legend>
              {celulas.map((celula) => (
                <label
                  key={celula.codigo}
                  className="flex items-center gap-3 min-h-[56px] px-4 bg-slate-950 border border-slate-700 rounded-xl text-base"
                >
                  <input
                    type="checkbox"
                    className="h-6 w-6"
                    checked={selecionadas.includes(celula.codigo)}
                    onChange={() => alternar(celula.codigo)}
                  />
                  <span className="font-bold text-white">{celula.nome}</span>
                  <span className="text-xs font-mono text-slate-400">{celula.codigo}</span>
                </label>
              ))}
            </fieldset>

            <button
              type="button"
              disabled={salvando}
              onClick={() => void vincular()}
              className="min-h-[64px] w-full bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold text-base rounded-xl touch-manipulation"
            >
              Vincular este tablet
            </button>
          </section>
        )}
      </div>
    </div>
  );
}
